import React from 'react';
import {
    Box,
    Container,
    Typography,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Card,
    CardContent,
    Skeleton,
    Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { scrollToSection } from '../utils/utils';

const AboutSection = ({ data }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const isLoading = data === undefined;

    const homeSectionPaths = new Set(['/', '/portfolio', '/services', '/about_us', '/contact']);

    const handleContactNav = () => {
        if (homeSectionPaths.has(location.pathname)) {
            scrollToSection('contact', () => { });
            window.history.pushState({}, '', '/contact');
        } else {
            navigate('/contact', { state: { scrollTo: 'contact' } });
        }
    };

    const stats = [
        { key: 'project_delivered', icon: <EmojiEventsOutlinedIcon /> },
        { key: 'happy_clients', icon: <PeopleAltOutlinedIcon /> },
        { key: 'countries_served', icon: <PublicOutlinedIcon /> },
        { key: 'client_satisfaction', icon: <TaskAltIcon /> },
    ];

    const points =
        data?.meta?.points?.length
            ? data.meta.points
            : t('pages.home.about.points', { returnObjects: true });

    return (
        <Box id="about" sx={{ py: 10, backgroundColor: theme.palette.background.default }}>
            <Container maxWidth="xl">
                {/* MAIN LAYOUT */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 6,
                        alignItems: 'center',
                    }}
                >
                    {/* LEFT SECTION */}
                    <Box sx={{ flex: 1 }}>
                        <Chip
                            label={t('pages.home.about.chip_title')}
                            sx={{
                                fontFamily: "Syne, sans-serif",
                                backgroundColor: theme.palette.background.chip,
                                color: theme.palette.custom.chipText,
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                lineHeight: "1.25rem",
                                mb: 2,
                                p: 1,
                            }}
                        />

                        <Typography
                            variant="h3"
                            fontWeight="bold"
                            sx={{
                                background: theme.palette.custom.headingGradient,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                mb: 3,
                                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3rem' },
                                lineHeight: { xs: '2rem', sm: '2.25rem', md: '2.5rem', lg: '1' },
                            }}
                        >
                            {isLoading ? (
                                <Skeleton variant="text" height={60} width="80%" />
                            ) : (
                                data?.meta?.title || t('pages.home.about.headline')
                            )}
                        </Typography>

                        <Typography sx={{ 
                            mb: 3, typography: 'textLg', color: 'text.secondary', 
                            wordBreak: "break-word", overflowWrap: "break-word" 
                        }}>
                             {isLoading ? (
                                 <Skeleton variant="text" height={40} width="90%" />
                             ) : (
                                 data?.meta?.description_1 || t('pages.home.about.desc_1')
                             )}
                         </Typography>

                        <Typography sx={{ 
                            mb: 4, typography: 'textLg', color: 'text.secondary', 
                            wordBreak: "break-word", overflowWrap: "break-word" 
                        }}>
                             {isLoading ? (
                                 <Skeleton variant="text" height={40} width="90%" />
                             ) : (
                                 data?.meta?.description_2 || t('pages.home.about.desc_2')
                             )}
                         </Typography>

                        <List>
                            {isLoading ? (
                                <PointsSkeleton />
                            ) : (
                                points.slice(0, 4).map((point, index) => (
                                    <ListItem key={index} sx={{ mb: 1, px: 0 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <TaskAltIcon
                                                sx={{
                                                    color: theme.palette.text.white,
                                                    background: theme.palette.custom.serviceIconBg,
                                                    borderRadius: '50%',
                                                    p: '3px',
                                                }}
                                            />
                                        </ListItemIcon>
                                         <ListItemText
                                             primary={point}
                                             primaryTypographyProps={{
                                                 fontWeight: 400,
                                                 noWrap: false,
                                                 sx: {
                                                     display: 'block',
                                                     width: '100%',
                                                     wordBreak: "break-word"
                                                 }
                                             }}
                                         />
                                    </ListItem>
                                ))
                            )}
                        </List>

                        <Button
                            endIcon={<ArrowForwardIcon />}
                            variant="contained"
                            // onClick={() => {
                            //     window.open('https://nexuslinkservices.com/contact-us', '_blank');
                            // }}
                            onClick={handleContactNav}
                            sx={{
                                mt: 2,
                                px: 4,
                                backgroundImage: theme.palette.custom.buttonGradient,
                                color: theme.palette.common.white,
                                fontWeight: '500',
                                textTransform: 'none',
                                borderRadius: '8px',
                                py: 1.5,
                                boxShadow: 'none',
                                typography: 'textSm',
                            }}
                        >
                            {t('pages.home.about.button.contact_us')}
                        </Button>
                    </Box>

                    {/* RIGHT SECTION */}
                    <Box sx={{ flex: 1 }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(2, 1fr)', // mobile → 2 cards
                                    sm: 'repeat(2, 1fr)', // tablet → 2 cards
                                },
                                gap: { xs: 2, md: 4 },
                            }}
                        >
                            {stats.map((stat) => (
                                <Card
                                    key={stat.key}
                                    sx={{
                                        height: '100%',
                                        borderRadius: '20px',
                                        boxShadow: theme.palette.custom.cardShadow,
                                        border: '0.5px solid',
                                        borderColor: 'divider',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            //   transform: 'translateY(-6px)',
                                            boxShadow: theme.palette.custom.cardShadowHover,
                                            borderColor: theme.palette.primary.main,
                                            '& .about-icon': {
                                                transform: 'scale(1.15)',
                                            },
                                        },
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            p: { xs: 2, md: 4 },
                                            textAlign: 'center',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box
                                            className="about-icon"
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: '14px',
                                                background: theme.palette.custom.serviceIconBg,
                                                color: theme.palette.custom.serviceIconColor,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 1,
                                                transition: 'transform 0.3s ease-in-out',
                                            }}
                                        >
                                            {React.cloneElement(stat.icon, { fontSize: 'large' })}
                                        </Box>

                                        <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: "#000000ff" }}>
                                            {isLoading ? (
                                                <Skeleton variant="text" height={20} width={120} />
                                            ) : (
                                                data?.meta?.[stat.key] || t(`pages.home.about.stats.${stat.key}.value`)
                                            )}
                                        </Typography>

                                        <Typography color="text.secondary" typography="textSm">
                                            {isLoading ? (
                                                <Skeleton variant="text" height={20} width={120} />
                                            ) : (
                                                t(`pages.home.about.stats.${stat.key}.label`)
                                            )}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

const PointsSkeleton = () => (
    <>
        {[...Array(4)].map((_, i) => (
            <ListItem key={i} disablePadding sx={{ mb: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                    <Skeleton variant="circular" width={26} height={26} />
                </ListItemIcon>
                <ListItemText
                    primary={<Skeleton variant="text" width={`${70 + (i * 5)}%`} height={26} />}
                />
            </ListItem>
        ))}
    </>
);


export default AboutSection;
